import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/layout/WhatsAppButton';
import { QuoteDialog, ProductDialog } from '../components/Modals';
import { listItems, fileUrl, getClientInfo, isClient } from '../api';
import { CATEGORIES, findCategory } from '../mock';
import { ArrowUpRight, ChevronRight, ChevronLeft, Home as HomeIcon, X, ZoomIn, ImageOff } from 'lucide-react';

export default function ProductsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cameFromCampaign, setCameFromCampaign] = useState(false);
  const clientInfo = getClientInfo();

  const catParam = new URLSearchParams(location.search).get('categorie');
  const activeCat = findCategory(catParam);

  useEffect(() => {
    if (!isClient()) {
      const q = catParam ? `?categorie=${encodeURIComponent(catParam)}` : '';
      navigate(`/cont${q}`, { replace: true });
      return;
    }
    listItems('products').then(setProducts).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCameFromCampaign(!!catParam && !!getClientInfo());
  }, [catParam]);

  const shown = useMemo(() => {
    if (!activeCat) return products;
    return products.filter(activeCat.match);
  }, [products, activeCat]);

  const openQuote = (product = null) => {
    setPrefill(product && product.name ? product : null);
    setQuoteOpen(true);
  };

  const fromCampaign = !!clientInfo && cameFromCampaign && activeCat;
  const otherCats = CATEGORIES.filter((c) => !activeCat || c.key !== activeCat.key);

  // Aggregate all real uploaded photos across products in this category
  const photos = useMemo(() => {
    const out = [];
    shown.forEach((p) => {
      const foto = (p.media && p.media.foto) || [];
      foto.forEach((f) => f && f.url && out.push({ url: f.url, name: f.name || p.name, product: p.name }));
    });
    return out;
  }, [shown]);

  const [lbIndex, setLbIndex] = useState(null);
  const showLb = lbIndex !== null && photos[lbIndex];
  const closeLb = () => setLbIndex(null);
  const prevLb = (e) => { if (e) e.stopPropagation(); setLbIndex((i) => (i - 1 + photos.length) % photos.length); };
  const nextLb = (e) => { if (e) e.stopPropagation(); setLbIndex((i) => (i + 1) % photos.length); };

  useEffect(() => {
    if (lbIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLbIndex(null);
      if (e.key === 'ArrowLeft') setLbIndex((i) => (i - 1 + photos.length) % photos.length);
      if (e.key === 'ArrowRight') setLbIndex((i) => (i + 1) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lbIndex, photos.length]);

  return (
    <div className="bg-white min-h-screen" data-testid="products-page">
      <Header onQuote={() => openQuote()} />

      {/* Breadcrumb */}
      <div className="pt-24 pb-0">
        <div className="egea-container">
          <nav className="flex items-center gap-2 text-[13px] text-slate-500" data-testid="breadcrumb">
            <Link to="/" className="flex items-center gap-1 hover:text-[#f5a524] transition-colors">
              <HomeIcon size={14} /> Acasă
            </Link>
            <ChevronRight size={14} className="text-slate-300" />
            <span className="text-slate-900 font-medium">{activeCat ? activeCat.title : 'Produse'}</span>
          </nav>
        </div>
      </div>

      {fromCampaign && (
        <div className="pt-4" data-testid="campaign-welcome-banner">
          <div className="egea-container">
            <div className="border-l-4 border-[#f5a524] bg-amber-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <div className="flex-1">
                <p className="text-[15px] text-slate-900 font-semibold">
                  {clientInfo?.name ? `Bine ai venit, ${clientInfo.name}` : 'Bine ai venit'}
                </p>
                <p className="text-[13px] text-slate-600 mt-0.5">
                  Ți-am pregătit gama {activeCat.title}. Cere o ofertă tehnică — datele tale sunt deja completate.
                </p>
              </div>
              <button onClick={() => openQuote()} data-testid="campaign-welcome-quote" className="btn-accent px-5 py-2.5 text-[13px] uppercase tracking-wide whitespace-nowrap self-start sm:self-auto">
                Cere ofertă
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category hero with generic image */}
      <section className="pt-8 pb-0">
        <div className="egea-container">
          <div className="relative overflow-hidden border border-slate-200 h-[260px] md:h-[400px]" data-testid="category-hero">
            {activeCat && (
              <img src={activeCat.image} alt={activeCat.title} className="absolute inset-0 w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-slate-950/10" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7 md:p-10">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.25em] text-[#f5a524] mb-3">
                {activeCat ? 'Categorie' : 'Catalog'}
              </span>
              <h1 className="font-display font-black uppercase tracking-tight text-white text-4xl md:text-6xl leading-[1]">
                {activeCat ? activeCat.title : 'Produsele noastre'}
              </h1>
              <p className="mt-3 text-base text-slate-200 max-w-[560px] leading-relaxed">
                {activeCat ? activeCat.subtitle : 'Alege o categorie pentru a vedea produsele relevante.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Photo gallery for the category */}
      {activeCat && (
        <section className="pt-12 pb-4" data-testid="category-gallery">
          <div className="egea-container">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <span className="section-eyebrow">Galerie foto</span>
                <h2 className="font-display font-bold uppercase tracking-tight text-slate-900 text-2xl md:text-3xl mt-2">
                  Imagini {activeCat.title}
                </h2>
              </div>
              {photos.length > 0 && (
                <span className="text-[13px] text-slate-400 whitespace-nowrap">{photos.length} imagini</span>
              )}
            </div>
            {photos.length === 0 ? (
              <div className="border border-dashed border-slate-300 bg-slate-50 py-12 px-6 text-center">
                <ImageOff size={30} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 text-[14px] max-w-[420px] mx-auto">
                  Momentan nu avem fotografii publicate pentru {activeCat.title}. Cere o ofertă și îți trimitem gama completă cu imagini.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="gallery-grid">
                {photos.map((f, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setLbIndex(i)}
                    data-testid={`gallery-photo-${i}`}
                    className="group relative aspect-square overflow-hidden border border-slate-200 bg-slate-100"
                  >
                    <img src={fileUrl(f.url)} alt={f.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/30 transition-colors flex items-center justify-center">
                      <ZoomIn size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    {f.product && (
                      <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white/90 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 truncate text-left">
                        {f.product}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Category chooser when no category selected */}
      {!activeCat && (
        <section className="py-12">
          <div className="egea-container grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="category-chooser">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={`/produse?categorie=${encodeURIComponent(c.key)}`} className="group relative overflow-hidden border border-slate-200 bg-slate-900 min-h-[240px] flex flex-col justify-end p-6">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
                <div className="relative z-10">
                  <h2 className="font-display font-bold uppercase text-white text-2xl">{c.title}</h2>
                  <span className="mt-2 inline-flex items-center gap-2 text-sm font-bold uppercase text-white">Vezi produse <ArrowUpRight size={16} className="text-[#f5a524]" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Products grid */}
      {activeCat && (
        <section className="pt-8 pb-14">
          <div className="egea-container">
            <div className="mb-6">
              <span className="section-eyebrow">Produse</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-slate-900 text-2xl md:text-3xl mt-2">
                Gama {activeCat.title}
              </h2>
            </div>
            {shown.length === 0 ? (
              <div className="border border-dashed border-slate-300 bg-slate-50 py-16 px-6 text-center">
                <p className="text-slate-600 text-[15px] max-w-[440px] mx-auto">
                  Momentan nu avem produse publicate în această categorie. Cere o ofertă și îți trimitem gama completă {activeCat.title}.
                </p>
                <button onClick={() => openQuote()} data-testid="empty-quote-btn" className="btn-accent px-6 py-3 text-[13px] uppercase tracking-wide mt-6 inline-flex items-center gap-2">
                  Cere ofertă <ArrowUpRight size={16} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
                {shown.map((p, idx) => (
                  <button
                    key={p.id}
                    onClick={() => setSelected(p)}
                    data-testid={`product-card-${p.id}`}
                    className="group text-left card-hover border border-slate-200 bg-white overflow-hidden flex flex-col shadow-sm"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={fileUrl(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute top-3 left-3 text-[10px] tracking-[0.15em] uppercase font-bold text-slate-900 bg-[#f5a524] px-2.5 py-1">
                        {String(idx + 1).padStart(2, '0')} · {p.category}
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-[11px] text-slate-400 mb-2">{p.tag}</div>
                      <h3 className="font-display font-semibold text-slate-900 text-lg leading-snug mb-1.5">{p.name}</h3>
                      <p className="text-[13px] text-slate-500 mb-4">{p.useCase}</p>
                      <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-200">
                        <span className="font-display font-bold text-[#d97706] text-[14px]">{p.price}</span>
                        <ArrowUpRight size={18} className="text-slate-400 group-hover:text-[#f5a524] transition-colors" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Switch category strip */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="egea-container">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <span className="section-eyebrow">Navigare</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-slate-900 text-2xl md:text-3xl mt-2">
                Alege altă categorie
              </h2>
            </div>
            <Link to="/" data-testid="back-home-link" className="btn-ghost px-5 py-2.5 text-[13px] uppercase tracking-wide flex items-center gap-1.5 self-start">
              <HomeIcon size={15} /> Acasă
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="switch-category">
            {otherCats.map((c) => (
              <Link
                key={c.key}
                to={`/produse?categorie=${encodeURIComponent(c.key)}`}
                data-testid={`switch-to-${c.key}`}
                className="group flex items-center justify-between border border-slate-200 bg-white px-6 py-5 hover:border-[#f5a524] transition-colors shadow-sm"
              >
                <div>
                  <h3 className="font-display font-bold uppercase text-slate-900 text-lg">{c.title}</h3>
                  <p className="text-[13px] text-slate-500 mt-1 max-w-[360px]">{c.subtitle}</p>
                </div>
                <ArrowUpRight size={20} className="text-slate-400 group-hover:text-[#f5a524] transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fullscreen lightbox with navigation */}
      {showLb && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" data-testid="gallery-lightbox" onClick={closeLb}>
          <button onClick={closeLb} data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white z-10">
            <X size={28} />
          </button>
          {photos.length > 1 && (
            <>
              <button onClick={prevLb} data-testid="lightbox-prev" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors z-10">
                <ChevronLeft size={26} />
              </button>
              <button onClick={nextLb} data-testid="lightbox-next" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors z-10">
                <ChevronRight size={26} />
              </button>
            </>
          )}
          <figure className="max-w-[92vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(photos[lbIndex].url)} alt={photos[lbIndex].name} className="max-w-full max-h-[82vh] object-contain" />
            <figcaption className="text-white/70 text-[13px] mt-4 text-center">
              {photos[lbIndex].product ? `${photos[lbIndex].product} · ` : ''}{lbIndex + 1} / {photos.length}
            </figcaption>
          </figure>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
      <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} prefillProduct={prefill} prefillContact={clientInfo} />
      <ProductDialog product={selected} onClose={() => setSelected(null)} onQuote={openQuote} />
    </div>
  );
}
